import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import {
  persistSoftwareIqAnalyticsConsent,
  readSoftwareIqAnalyticsConsent,
  SOFTWAREIQ_ANALYTICS_CONSENT_EVENT,
  SOFTWAREIQ_ANALYTICS_PREFERENCES_EVENT,
  SoftwareIqConsentBanner,
} from '@softwareiq/brand-ui';
import {
  initializeAnalytics,
  setAnalyticsUserId,
  trackEvent,
  trackPageView,
  updateAnalyticsConsent,
} from '../../utils/analytics';

export default function AnalyticsProvider() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth0();
  const initialized = useRef(false);
  const lastFilterInteraction = useRef('');
  const [consent, setConsent] = useState(() => readSoftwareIqAnalyticsConsent());
  const [preferencesOpen, setPreferencesOpen] = useState(() => consent === null);

  useEffect(() => {
    initializeAnalytics();
    initialized.current = true;

    const openPreferences = () => setPreferencesOpen(true);
    const handleConsentChange = event => {
      const nextConsent = event.detail?.consent || null;
      setConsent(nextConsent);
      updateAnalyticsConsent(nextConsent);
    };
    const handlePreferenceClick = event => {
      if (!event.target.closest('[data-siq-cookie-preferences]')) return;
      event.preventDefault();
      openPreferences();
    };
    const handleFilterChange = event => {
      const filterBar = event.target.closest?.('[data-workspace-filter-bar]');
      if (!filterBar) return;
      const filterName = event.target.name || event.target.id || event.target.getAttribute?.('aria-label') || 'workspace_filter';
      const selectionCount = event.target.selectedOptions?.length
        ?? (event.target.type === 'checkbox' ? Number(event.target.checked) : 1);
      const interactionKey = `${window.location.pathname}:${filterName}:${selectionCount}`;
      if (lastFilterInteraction.current === interactionKey) return;
      lastFilterInteraction.current = interactionKey;
      trackEvent('filter_change', { filter_name: filterName, result_count: selectionCount });
    };
    window.addEventListener(SOFTWAREIQ_ANALYTICS_PREFERENCES_EVENT, openPreferences);
    window.addEventListener(SOFTWAREIQ_ANALYTICS_CONSENT_EVENT, handleConsentChange);
    document.addEventListener('click', handlePreferenceClick);
    document.addEventListener('change', handleFilterChange);
    return () => {
      window.removeEventListener(SOFTWAREIQ_ANALYTICS_PREFERENCES_EVENT, openPreferences);
      window.removeEventListener(SOFTWAREIQ_ANALYTICS_CONSENT_EVENT, handleConsentChange);
      document.removeEventListener('click', handlePreferenceClick);
      document.removeEventListener('change', handleFilterChange);
    };
  }, []);

  useEffect(() => {
    if (initialized.current) trackPageView();
  }, [location.pathname, location.search]);

  useEffect(() => {
    setAnalyticsUserId(consent === 'accepted' && isAuthenticated ? user?.sub : null);
  }, [consent, isAuthenticated, user?.sub]);

  const choose = nextConsent => {
    persistSoftwareIqAnalyticsConsent(nextConsent);
    setPreferencesOpen(false);
  };

  return (
    <SoftwareIqConsentBanner
      open={preferencesOpen}
      onAccept={() => choose('accepted')}
      onReject={() => choose('rejected')}
    />
  );
}
